import { z } from 'zod';
import { EXAMS } from './exam';
import { TOPICS } from './topics';
import { VoteTypeSchema } from './voting';

/**
 * Supported question types for the application.
 */
export enum QuestionTypeEnum {
  MULTIPLE_CHOICE = 'multiple_choice',
  TRUE_FALSE = 'true_false',
  FLASHCARD = 'flashcard',
  SORTING = 'sorting',
}

/**
 * Internal difficulty axis. Persisted and travels on the wire in this form;
 * see DIFFICULTY_LABELS for the human-facing rendering.
 */
export const DifficultySchema = z.enum(['easy', 'medium', 'hard']);
export type Difficulty = z.infer<typeof DifficultySchema>;

/**
 * Human-facing labels for {@link Difficulty}. UI code should render these
 * strings rather than the raw enum value so the wording stays consistent
 * across frontend, mobile, and any future surface.
 */
export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy: 'Basic',
  medium: 'Clinical',
  hard: 'Advanced',
};

/**
 * Discriminator content/render data for each question type.
 */

export interface QuestionContentMultipleChoice {
  options: string[];
}

export interface QuestionContentTrueFalse {
  labels?: { true: string; false: string };
}

export interface QuestionContentFlashcard {
  backText?: string; // Loaded from answer.text on public sanitize
  /** Set on public sanitize when the server can grade a typed answer; absent means self-grade only. */
  guidedGrading?: boolean;
}

export interface QuestionContentSorting {
  items: { id: string; text: string }[];
}

export type QuestionContent =
  | ({ type: QuestionTypeEnum.MULTIPLE_CHOICE } & QuestionContentMultipleChoice)
  | ({ type: QuestionTypeEnum.TRUE_FALSE } & QuestionContentTrueFalse)
  | ({ type: QuestionTypeEnum.FLASHCARD } & QuestionContentFlashcard)
  | ({ type: QuestionTypeEnum.SORTING } & QuestionContentSorting);

/**
 * Answer payload definitions (what the client sends and backend stores as correct answer).
 */

export interface QuestionAnswerMultipleChoice {
  correctOptionIndex: number;
}

export interface QuestionAnswerTrueFalse {
  correct: boolean;
}

export interface QuestionAnswerFlashcard {
  text: string;
}

export interface QuestionAnswerSorting {
  correctOrder: string[]; // Array of item IDs in correct order
}

export type QuestionAnswer =
  | ({ type: QuestionTypeEnum.MULTIPLE_CHOICE } & QuestionAnswerMultipleChoice)
  | ({ type: QuestionTypeEnum.TRUE_FALSE } & QuestionAnswerTrueFalse)
  | ({ type: QuestionTypeEnum.FLASHCARD } & QuestionAnswerFlashcard)
  | ({ type: QuestionTypeEnum.SORTING } & QuestionAnswerSorting);

/**
 * Helper to extract specific content/answer type based on QuestionTypeEnum
 */
export type GetQuestionContent<T extends QuestionTypeEnum> = Extract<QuestionContent, { type: T }>;
export type GetQuestionAnswer<T extends QuestionTypeEnum> = Extract<QuestionAnswer, { type: T }>;

/**
 * Citation metadata for medical images sourced from open-access journals.
 * Present only when the image came from a photographic source (PMC / OpenI);
 * null or absent for locally-rendered diagrams. See IMAGE_MCQ_SPEC.md §2.
 */
export const ImageCitationSchema = z.object({
  authors: z.string(),
  title: z.string(),
  journal: z.string(),
  year: z.string(),
  pmc_id: z.string(),
  doi: z.string().nullable(),
  license: z.string(),
});
export type ImageCitation = z.infer<typeof ImageCitationSchema>;

/**
 * Image-question fields. All optional — a question without these is a
 * normal text-only question. When present, `imageUrl` is a public Firebase
 * Storage URL. Mix-in for `Question` and `QuestionViewData`.
 *
 * See ~/Desktop/IMAGE_MCQ_SPEC.md for the storage convention and modality
 * vocabulary.
 *
 * There is deliberately no caption field. The pipeline's figure caption is the
 * source article's own and names the diagnosis in most cards; it stays inside
 * the pipeline (gates, review page) and never reaches the learner.
 */
export const ImageQuestionFieldsSchema = z.object({
  imageUrl: z.string().url().optional(),
  // Modality vocabulary is controlled by the upstream pipeline
  // (~/Desktop/question_gen_openi/) and may grow; typed loose for forward
  // compatibility. Common: 'X-ray','CT','MRI','Ultrasound','ECG',
  // 'Histopathology','Diagram','Plot' — see IMAGE_MCQ_SPEC §2.
  imageModality: z.string().optional(),
  imageCitation: ImageCitationSchema.nullable().optional(),
  // 'rendered' = locally drawn (diagram/plot/ECG/structure); 'photographic' = PMC/OpenI.
  renderSource: z.enum(['rendered', 'photographic']).optional(),
  // Which local renderer produced the image; only set when renderSource === 'rendered'.
  // 'molecule' = RDKit structure (question_gen_openi renderers/molecule.py).
  renderer: z.enum(['pathway', 'plot', 'ecg', 'molecule']).optional(),
});
export type ImageQuestionFields = z.infer<typeof ImageQuestionFieldsSchema>;

/**
 * LLM-graded flashcard answer request/response
 */
export const FlashcardGradeRequestSchema = z.object({
  userAnswer: z.string(),
  /** As on the answer route: a quiz may answer again (it is graded at the end); a feed or
   *  question-screen answer is one per question and counts toward feed progress. */
  isQuiz: z.boolean().optional(),
});
export type FlashcardGradeRequest = z.infer<typeof FlashcardGradeRequestSchema>;

export const FlashcardGradeResponseSchema = z.object({
  isCorrect: z.boolean(),
  feedback: z.string(),
  correctAnswer: z.string(),
});
export type FlashcardGradeResponse = z.infer<typeof FlashcardGradeResponseSchema>;

// 'deleted' is a moderation soft-delete: the backend read layer (isAllowedStatus) excludes it,
// so a removed question drops out of feeds/trending/new quizzes while its doc — and every
// reference to it (quizzes, bookmarks, votes, comments) — stays intact. See
// docs/comment-moderation-plan.md.
export const QuestionStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'published',
  'deleted',
]);
export type QuestionStatus = z.infer<typeof QuestionStatusSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Canonical per-type content/answer schemas — the SINGLE SOURCE for the question
// write/gate bodies. The backend question-engine strategies import these, and
// the per-type branches of the read `QuestionSchema` below reuse them, so the
// validation gate and the read shape can't drift from each other (or from a
// third hand-written copy). `.strict()` so the gate REJECTS an unexpected
// content/answer field (e.g. an upstream generator rename) rather than silently
// stripping it — applied to the per-type bodies ONLY, never the top-level
// question doc, which legitimately carries loose generator sidecars
// (metadata.*, usage traces, vision/quality diagnostics).
// ─────────────────────────────────────────────────────────────────────────────

export const MultipleChoiceContentSchema = z
  .object({ options: z.array(z.string()).min(2, 'Multiple choice must have at least 2 options') })
  .strict();
export const MultipleChoiceAnswerSchema = z
  .object({
    correctOptionIndex: z.number().int().min(0, 'Correct option index cannot be negative'),
  })
  .strict();

export const TrueFalseContentSchema = z
  .object({ labels: z.object({ true: z.string(), false: z.string() }).optional() })
  .strict();
export const TrueFalseAnswerSchema = z.object({ correct: z.boolean() }).strict();

// Flashcard write/gate content is empty — the card "back" travels in
// `answer.text` and is projected to `content.backText` only on public sanitize
// (the read/serve side; see the FLASHCARD branch of QuestionSchema below).
export const FlashcardContentSchema = z.object({}).strict();
export const FlashcardAnswerSchema = z
  .object({ text: z.string().min(1, 'Flashcard must have an answer') })
  .strict();

export const SortingContentSchema = z
  .object({
    items: z
      .array(z.object({ id: z.string(), text: z.string() }).strict())
      .min(2, 'Sorting question must have at least 2 items'),
  })
  .strict();
export const SortingAnswerSchema = z.object({ correctOrder: z.array(z.string()).min(2) }).strict();

/**
 * Read/serve shape of a question — the canonical NESTED form the backend serves
 * and the app consumes: top-level `questionType` (discriminator) with nested
 * `content` + `answer` bodies, plus DB/social/taxonomy fields and the image
 * mix-in. Supersedes the legacy flat `QuestionViewData` (quiz.ts).
 *
 * This is the READ counterpart to the write/DB contract (`BaseQuestionSchema` +
 * the backend's discriminated `questionSchema`). Modeled as a
 * `z.discriminatedUnion` on `questionType`, so `content`/`answer` narrow to the
 * right per-type body once `questionType` is checked — consumers no longer need
 * `(question.content as any)` casts, and (unlike the previous non-discriminated
 * `z.union`) a body can't be silently validated against the wrong variant.
 * `content`/`answer` are `Omit<QuestionContent|QuestionAnswer, 'type'>` — the
 * `type` discriminator lives at `questionType`.
 */
// Shared read fields carried by every question, independent of type. The four
// per-type branches below extend this with a `questionType` literal + the
// narrowed `content`/`answer` bodies for that type.
const QuestionReadBaseSchema = z
  .object({
    id: z.string().optional(),
    text: z.string(),
    difficulty: DifficultySchema,
    explanation: z.string().optional(),
    createdBy: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    upvotes: z.number().optional(),
    downvotes: z.number().optional(),
    score: z.number().optional(),
    voteCount: z.number().optional(),
    status: QuestionStatusSchema.optional(),
    userVote: VoteTypeSchema.optional(),
    isBookmarked: z.boolean().optional(),
    // Taxonomy fields (three-axis contract; see docs/taxonomy-axes.md)
    //   Axis 1 (identity): specialty + subSpecialty
    //   Axis 2 (topics):   topics[]   (blueprint L1 vocab)
    //   Axis 3 (exams):    forExams[] (where this shows up on boards)
    specialty: z.string(),
    subSpecialty: z.string().nullable().optional(),
    topics: z.array(z.string()),
    subtopics: z.array(z.string()).optional(),
    forExams: z.array(z.enum(EXAMS)),
    references: z.array(z.string()).optional(),
    pitfalls: z.array(z.string()).optional(),
    clinicalPearls: z.array(z.string()).optional(),
    backToBasics: z.array(z.string()).optional(),
  })
  .merge(ImageQuestionFieldsSchema);

export const QuestionSchema = z.discriminatedUnion('questionType', [
  QuestionReadBaseSchema.extend({
    questionType: z.literal(QuestionTypeEnum.MULTIPLE_CHOICE),
    content: MultipleChoiceContentSchema,
    answer: MultipleChoiceAnswerSchema.optional(),
  }),
  QuestionReadBaseSchema.extend({
    questionType: z.literal(QuestionTypeEnum.TRUE_FALSE),
    content: TrueFalseContentSchema,
    answer: TrueFalseAnswerSchema.optional(),
  }),
  QuestionReadBaseSchema.extend({
    questionType: z.literal(QuestionTypeEnum.FLASHCARD),
    // Serve shape: `backText` is projected from `answer.text` on public sanitize
    // (see backend FlashcardStrategy); the write/gate content is empty
    // (FlashcardContentSchema). This read body is the one genuine read≠write
    // divergence, so it stays local instead of reusing FlashcardContentSchema.
    content: z
      .object({ backText: z.string().optional(), guidedGrading: z.boolean().optional() })
      .strict(),
    answer: FlashcardAnswerSchema.optional(),
  }),
  QuestionReadBaseSchema.extend({
    questionType: z.literal(QuestionTypeEnum.SORTING),
    content: SortingContentSchema,
    answer: SortingAnswerSchema.optional(),
  }),
]);
export type Question = z.infer<typeof QuestionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Canonical Zod schemas — the single source of truth for the question write
// shape. The backend's type-specific discriminated union (`questionSchema`)
// extends `BaseQuestionSchema`, and the seeders validate against it, so the
// shape can't drift between the generator, the API gate, and the app.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Canonical flat question shape (the shared write-contract). Enum-enforced on
 * the generated taxonomy axes (`topics` ⊆ TOPICS, `forExams` ⊆ EXAMS);
 * `specialty` stays a string for now (specialty.ts isn't a literal-union const
 * yet). `subSpecialty` is FREE — the blueprint's "subspecialty" level maps to
 * `topics`, NOT to this field. `subtopics` carries the blueprint leaf (the
 * 3-level `specialty → topics → subtopics` taxonomy). Kept a plain `z.object`
 * (no `.strict()`/effects) so the backend can `.extend()`/`.merge()` it.
 */
export const BaseQuestionSchema = z
  .object({
    text: z.string().min(10, 'Question text must be at least 10 characters'),
    explanation: z.string().min(20, 'Explanation must be at least 20 characters'),
    specialty: z.string().min(1, 'Specialty is required'),
    subSpecialty: z.string().optional(),
    topics: z.array(z.enum(TOPICS)).optional(),
    subtopics: z.array(z.string()).optional(),
    forExams: z.array(z.enum(EXAMS)).optional(),
    difficulty: DifficultySchema,
    references: z.array(z.string()).optional(),
    pitfalls: z.array(z.string()).optional(),
    clinicalPearls: z.array(z.string()).optional(),
    backToBasics: z.array(z.string()).optional(),
  })
  .merge(ImageQuestionFieldsSchema);
export type BaseQuestion = z.infer<typeof BaseQuestionSchema>;

/**
 * Persistence envelope — DB-managed fields a stored question carries on top of
 * the base + type-specific fields. Timestamp fields are loose (server-set
 * FieldValue sentinels), validated only for presence.
 */
export const QuestionDbFieldsSchema = z.object({
  id: z.string().optional(),
  createdBy: z.string(),
  status: QuestionStatusSchema,
  reviewedBy: z.string().optional(),
  reviewedAt: z.unknown().optional(),
  createdAt: z.unknown().optional(),
  upvotes: z.number().optional(),
  downvotes: z.number().optional(),
  voteCount: z.number().optional(),
  score: z.number().optional(),
  userVote: VoteTypeSchema.optional(),
  randomSeed: z.number().optional(),
  commentCount: z.number().optional(),
  stats: z
    .object({
      totalResponses: z.number(),
      answerCounts: z.array(z.number()).optional(),
      correctResponses: z.number().optional(),
    })
    .optional(),
});
export type QuestionDbFields = z.infer<typeof QuestionDbFieldsSchema>;
